import { normalizeLobbyCode } from "@cold-case/shared/lobby-city-codes";
import { lobbyMemberDisplayLabel } from "@cold-case/shared/player-display-name";
import { isRandomCharacterId } from "@cold-case/shared/playable-character";

import {
  LANDING_TAGLINE,
  REGISTER_DISPLAY_NAME_HINT,
} from "../content/landing-copy.js";
import {
  hubOutcomeBannerHtml,
  type PostCaseOutcome,
} from "../content/post-case-outcome.js";
import {
  buildLobbyJoinUrl,
  clearJoinCodeFromLocation,
  readJoinCodeFromLocation,
  shareLobbyInvite,
} from "../lib/lobby-invite.js";
import {
  bindDetectivePicker,
  lobbyMemberCardHtml,
  preloadDetectivePortraits,
  renderDetectivePickerHtml,
  type PlayableDetective,
} from "../lobby/detective-picker.js";
import { api, getToken, setToken } from "../net.js";
import { playerLabel } from "./display-name.js";
import { escapeHtml, viewBackButton } from "./html.js";
import type { MeProfile, PreGameView } from "./types.js";

const MAX_STARTING_POLLS = 15;
/** Abort lobby start POST if the server is still laying out the case. */
const START_CASE_TIMEOUT_MS = 30_000;

export type PreGameDeps = {
  formatApiError: (data: unknown, fallback: string) => string;
  hideGameHost: () => void;
  hideSessionLoading: () => void;
  dismissAllGameLoading: () => void;
  clearPostCaseBanner: () => void;
  consumePostCaseOutcome: () => PostCaseOutcome | null;
  connectLobbySocket: (code: string) => void;
  teardownLobbySocket: () => void;
  setConnectionBanner: (visible: boolean, text?: string) => void;
  ensureGameSocket: (code: string) => void;
  tryStartFromLobbyAfterRoulette: (
    sessionId: string,
    reveal?: {
      chosenTitle: string;
      catalog: { total: number; poolTotal?: number; titles: string[] };
    } | null
  ) => Promise<void>;
  fetchLobbySessionReveal: (
    lobbyCode: string
  ) => Promise<{
    chosenTitle: string;
    catalog: { total: number; poolTotal?: number; titles: string[] };
  } | null>;
  prefetchSession: (sessionId: string) => void;
  checkSessionAvailable: (sessionId: string) => Promise<boolean>;
  abandonStaleLobbySession: (lobbyCode: string, message?: string) => Promise<void>;
  clearLobbySessionStartBlocks: () => void;
  leaveHubSubflow: () => void;
  abandonCurrentLobby: () => Promise<void>;
  readRejoinHint: () => { lobbyCode: string; sessionId: string } | null;
  clearRejoinHint: () => void;
  getUserId: () => string;
  setUserId: (id: string) => void;
  onEnterGame: () => void;
};

export type PreGameController = ReturnType<typeof createPreGameController>;

export function createPreGameController(deps: PreGameDeps) {
  let me: MeProfile | null = null;
  let currentView: PreGameView = "landing";
  let refreshLobbyView: ((code: string) => Promise<void>) | null = null;
  let characterSaveTimer: ReturnType<typeof setTimeout> | null = null;
  let displayNameSaveTimer: ReturnType<typeof setTimeout> | null = null;
  let lastSavedLobbyDisplayName: string | null = null;
  let lastLobbyHostUserId: string | null = null;
  const startingPollCounts = new Map<string, number>();
  let detectivesRoster: PlayableDetective[] = [];
  let lobbyChatLogEl: HTMLDivElement | null = null;
  let pendingJoinCode: string | null = null;
  let lobbyShellCode: string | null = null;
  let lobbyShellMaxPlayers: number | null = null;
  let characterSaveInFlight = false;

  function isSoloLobby(maxPlayers: number): boolean {
    return maxPlayers === 1;
  }

  function lobbyInviteBlockHtml(code: string): string {
    const inviteUrl = buildLobbyJoinUrl(code);
    return `
      <div class="lobby-invite-block">
        <p class="lobby-invite-label">Invite detectives</p>
        <button type="button" class="lobby-invite-link" data-lobby-invite>${escapeHtml(inviteUrl)}</button>
        <p class="lobby-invite-hint">Tap to copy and share · City code: <strong class="lobby-city-code">${escapeHtml(code)}</strong></p>
      </div>`;
  }

  function lobbyChatBlockHtml(): string {
    return `
        <div class="lobby-chat">
          <p class="lobby-chat-label">Lobby chat</p>
          <div class="lobby-chat-log" id="lobby-chat-log" role="log" aria-live="polite"></div>
          <form class="lobby-chat-form" id="lobby-chat-form">
            <input id="lobby-chat-input" type="text" maxlength="500" placeholder="Say hello…" autocomplete="off" />
            <button type="submit" class="secondary">Send</button>
          </form>
        </div>`;
  }

  const FALLBACK_DETECTIVES: PlayableDetective[] = [
    { npc_id: "pc_knight_m", display_name: "Knight", gender: "male" },
    { npc_id: "pc_princess", display_name: "Princess", gender: "female" },
  ];

  function el(id: string): HTMLElement {
    return document.getElementById(id)!;
  }

  function authPanel(): HTMLElement {
    return el("auth-panel");
  }

  function lobbyPanel(): HTMLElement {
    return el("lobby-panel");
  }

  function showAuthPanel(): void {
    authPanel().classList.remove("hidden");
    lobbyPanel().classList.add("hidden");
    deps.hideGameHost();
  }

  function showLobbyPanel(): void {
    authPanel().classList.add("hidden");
    lobbyPanel().classList.remove("hidden");
    lobbyPanel().classList.add("panel-wide");
  }

  function setMsg(text: string): void {
    const msg = document.getElementById("pre-game-msg");
    if (msg) msg.textContent = text;
  }

  function bindBack(handler: () => void): void {
    authPanel()
      .querySelectorAll("[data-action='back']")
      .forEach((btn) => {
        btn.addEventListener("click", handler);
      });
    lobbyPanel()
      .querySelectorAll("[data-action='back']")
      .forEach((btn) => {
        btn.addEventListener("click", handler);
      });
  }

  function renderLanding(): void {
    currentView = "landing";
    showAuthPanel();
    deps.dismissAllGameLoading();
    const outcomeHtml = (() => {
      const o = deps.consumePostCaseOutcome();
      return o ? hubOutcomeBannerHtml(o) : "";
    })();
    authPanel().innerHTML = `
      ${outcomeHtml}
      <header class="app-header">
        <h1 class="app-brand">Cold Case Solvers</h1>
        <p class="app-lead">${escapeHtml(LANDING_TAGLINE)}</p>
      </header>
      <div class="btn-stack">
        <button type="button" class="primary btn-xl" id="btn-guest">Play as guest</button>
        <button type="button" class="secondary btn-xl" id="btn-sign-in">Sign In</button>
        <button type="button" class="secondary btn-xl" id="btn-register">Register</button>
      </div>
      <p id="pre-game-msg" class="pre-game-msg" role="alert"></p>`;
    el("btn-guest").onclick = () => void guestPlay();
    el("btn-sign-in").onclick = () => renderSignIn();
    el("btn-register").onclick = () => renderRegister();
  }

  function renderSignIn(): void {
    currentView = "sign-in";
    showAuthPanel();
    authPanel().innerHTML = `
      <div class="view-toolbar">${viewBackButton()}<h2 class="view-title">Sign In</h2></div>
      <div class="form-stack">
        <label class="field-label">Username<input id="log-user" autocomplete="username" /></label>
        <label class="field-label">Password<input id="log-pass" type="password" autocomplete="current-password" /></label>
        <button type="button" class="primary btn-xl" id="btn-log">Sign In</button>
      </div>
      <p id="pre-game-msg" class="pre-game-msg" role="alert"></p>`;
    bindBack(() => renderLanding());
    el("btn-log").onclick = async () => {
      const username = (el("log-user") as HTMLInputElement).value;
      const password = (el("log-pass") as HTMLInputElement).value;
      const res = await api<{ token: string }>("/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });
      if (!res.ok) {
        setMsg("Login failed — check username and password.");
        return;
      }
      setToken((res.data as { token: string }).token);
      await showSignedInHub();
    };
  }

  function renderRegister(): void {
    currentView = "register";
    showAuthPanel();
    authPanel().innerHTML = `
      <div class="view-toolbar">${viewBackButton()}<h2 class="view-title">Register</h2></div>
      <div class="form-stack">
        <label class="field-label">Username<input id="reg-user" autocomplete="username" /></label>
        <label class="field-label">Password (6+)<input id="reg-pass" type="password" autocomplete="new-password" /></label>
        <label class="field-label">Display name <span class="field-hint">(${escapeHtml(REGISTER_DISPLAY_NAME_HINT)})</span>
          <input id="reg-display" autocomplete="nickname" placeholder="Optional" /></label>
        <button type="button" class="primary btn-xl" id="btn-reg">Create account</button>
      </div>
      <p id="pre-game-msg" class="pre-game-msg" role="alert"></p>`;
    bindBack(() => renderLanding());
    el("btn-reg").onclick = async () => {
      const username = (el("reg-user") as HTMLInputElement).value;
      const password = (el("reg-pass") as HTMLInputElement).value;
      const displayName = (el("reg-display") as HTMLInputElement).value.trim();
      const res = await api<{ token: string }>("/auth/register", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username,
          password,
          ...(displayName ? { displayName } : {}),
        }),
      });
      if (!res.ok) {
        setMsg(deps.formatApiError(res.data, "Registration failed."));
        return;
      }
      setToken((res.data as { token: string }).token);
      await showSignedInHub();
    };
  }

  async function guestPlay(): Promise<void> {
    const res = await api<{ token: string }>("/auth/guest", { method: "POST" });
    if (!res.ok) {
      setMsg(deps.formatApiError(res.data, "Guest sign-in failed."));
      return;
    }
    setToken((res.data as { token: string }).token);
    await showSignedInHub();
  }

  async function fetchMe(): Promise<MeProfile | null> {
    const res = await api<MeProfile>("/api/me");
    if (!res.ok || !getToken()) return null;
    return res.data as MeProfile;
  }

  function renderHubShell(
    welcome: string,
    buttonsHtml: string,
    extra = "",
    postCaseOutcome: PostCaseOutcome | null = null
  ): void {
    showLobbyPanel();
    const outcomeHtml = postCaseOutcome ? hubOutcomeBannerHtml(postCaseOutcome) : "";
    lobbyPanel().innerHTML = `
      ${outcomeHtml}
      <header class="app-header">
        <h1 class="app-brand">Cold Case Solvers</h1>
        <p class="hub-welcome">${escapeHtml(welcome)}</p>
      </header>
      ${extra}
      <div class="btn-stack hub-actions">${buttonsHtml}</div>
      <div id="lobby-room-host" class="hidden"></div>
      <p id="pre-game-msg" class="pre-game-msg" role="alert"></p>`;
  }

  async function showSignedInHub(): Promise<void> {
    const profile = await fetchMe();
    if (!profile) {
      setToken(null);
      renderLanding();
      setMsg("Session expired — sign in again.");
      return;
    }
    me = profile;
    deps.setUserId(profile.id);
    currentView = "hub";
    deps.onEnterGame();
    const welcome = `Welcome, ${playerLabel(profile)}!`;
    const buttons: string[] = [
      `<button type="button" class="primary btn-xl" data-nav="join">Join Game</button>`,
      `<button type="button" class="primary btn-xl" data-nav="host">Host Game</button>`,
    ];
    if (!profile.guest) {
      buttons.push(
        `<button type="button" class="secondary btn-xl" data-nav="reset-password">Reset Password</button>`,
        `<button type="button" class="secondary btn-xl" data-nav="history">Game History</button>`
      );
    }
    buttons.push(`<button type="button" class="secondary btn-xl" data-nav="sign-out">Sign Out</button>`);
    const rejoin = deps.readRejoinHint();
    const rejoinHtml = rejoin
      ? `<button type="button" class="secondary btn-xl" id="btn-rejoin">Rejoin last case</button>`
      : "";
    renderHubShell(welcome, buttons.join("") + rejoinHtml, "", deps.consumePostCaseOutcome());
    lobbyPanel().querySelectorAll("[data-nav]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const nav = (btn as HTMLElement).dataset.nav;
        if (nav === "join") renderJoinGame();
        else if (nav === "host") renderHostGame();
        else if (nav === "reset-password") renderResetPassword();
        else if (nav === "history") void renderGameHistory();
        else if (nav === "sign-out") signOut();
      });
    });
    document.getElementById("btn-rejoin")?.addEventListener("click", () => {
      if (!rejoin) return;
      void (async () => {
        const available = await deps.checkSessionAvailable(rejoin.sessionId);
        if (!available) {
          await deps.abandonStaleLobbySession(rejoin.lobbyCode);
          return;
        }
        deps.ensureGameSocket(rejoin.lobbyCode);
        void deps.tryStartFromLobbyAfterRoulette(rejoin.sessionId);
      })();
    });
    await ensureDetectivesRoster();
    stashJoinDeepLinkFromUrl();
    await consumeJoinDeepLink();
  }

  function signOut(): void {
    setToken(null);
    me = null;
    deps.clearRejoinHint();
    void deps.abandonCurrentLobby();
    renderLanding();
  }

  function resetLobbyState(): void {
    lobbyShellCode = null;
    lobbyShellMaxPlayers = null;
    lastLobbyHostUserId = null;
    lobbyChatLogEl = null;
    lastSavedLobbyDisplayName = null;
    characterSaveInFlight = false;
    if (characterSaveTimer) {
      clearTimeout(characterSaveTimer);
      characterSaveTimer = null;
    }
    if (displayNameSaveTimer) {
      clearTimeout(displayNameSaveTimer);
      displayNameSaveTimer = null;
    }
    startingPollCounts.clear();
  }

  function renderHostGame(): void {
    if (!me) return;
    void deps.abandonCurrentLobby();
    currentView = "host";
    renderHubShell(
      "Host a case",
      "",
      `
      <div class="view-toolbar">${viewBackButton()}<h2 class="view-title">Host Game</h2></div>
      <p class="app-lead">Choose how many detectives will work this case (including you).</p>
      <div class="player-count-grid">
        <button type="button" class="card-btn" data-players="1"><span class="card-btn-title">1 Player</span><span class="card-btn-sub">Solo investigation</span></button>
        <button type="button" class="card-btn" data-players="2"><span class="card-btn-title">2 Players</span><span class="card-btn-sub">Pair up</span></button>
      </div>`
    );
    bindBack(() => {
      deps.leaveHubSubflow();
      void showSignedInHub();
    });
    lobbyPanel().querySelectorAll("[data-players]").forEach((btn) => {
      btn.addEventListener("click", () => {
        const n = Number((btn as HTMLElement).dataset.players) as 1 | 2;
        void createLobby(n);
      });
    });
  }

  function renderJoinGame(): void {
    if (!me) return;
    currentView = "join";
    renderHubShell(
      "Join a case",
      "",
      `
      <div class="view-toolbar">${viewBackButton()}<h2 class="view-title">Join Game</h2></div>
      <p class="app-lead">Enter the city code from your host, or open their invite link.</p>
      <div class="form-stack">
        <label class="field-label">City code
          <input id="join-code" maxlength="12" autocomplete="off" autocapitalize="words" placeholder="Tokyo" />
        </label>
        <button type="button" class="primary btn-xl" id="btn-join">Join lobby</button>
      </div>`
    );
    bindBack(() => {
      deps.leaveHubSubflow();
      void showSignedInHub();
    });
    el("btn-join").onclick = () => void joinLobby();
    const pending = pendingJoinCode;
    if (pending) {
      const input = document.getElementById("join-code") as HTMLInputElement;
      input.value = pending;
    }
  }

  function renderResetPassword(): void {
    if (!me || me.guest) return;
    currentView = "reset-password";
    renderHubShell(
      "Reset password",
      "",
      `
      <div class="view-toolbar">${viewBackButton()}<h2 class="view-title">Reset Password</h2></div>
      <div class="form-stack">
        <label class="field-label">Current password<input id="pw-current" type="password" autocomplete="current-password" /></label>
        <label class="field-label">New password (6+)<input id="pw-new" type="password" autocomplete="new-password" /></label>
        <label class="field-label">Confirm new password<input id="pw-confirm" type="password" autocomplete="new-password" /></label>
        <button type="button" class="primary btn-xl" id="btn-pw-save">Update password</button>
      </div>`
    );
    bindBack(() => void showSignedInHub());
    el("btn-pw-save").onclick = async () => {
      const currentPassword = (el("pw-current") as HTMLInputElement).value;
      const newPassword = (el("pw-new") as HTMLInputElement).value;
      const confirm = (el("pw-confirm") as HTMLInputElement).value;
      if (newPassword.length < 6) {
        setMsg("New password must be at least 6 characters.");
        return;
      }
      if (newPassword !== confirm) {
        setMsg("New passwords do not match.");
        return;
      }
      const res = await api("/api/me/password", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      if (!res.ok) {
        setMsg(
          deps.formatApiError(res.data, "Could not update password — check your current password.")
        );
        return;
      }
      setMsg("Password updated.");
      window.setTimeout(() => void showSignedInHub(), 1200);
    };
  }

  async function renderGameHistory(): Promise<void> {
    if (!me || me.guest) return;
    currentView = "history";
    const res = await api<{
      entries: {
        playerCount: number;
        caseTitle: string;
        coPlayers: string[];
        playedAt: string;
        solved: boolean;
      }[];
    }>("/api/me/history");
    type HistoryEntry = {
      playerCount: number;
      caseTitle: string;
      coPlayers: string[];
      playedAt: string;
      solved: boolean;
    };
    const entries: HistoryEntry[] =
      res.ok && Array.isArray((res.data as { entries?: unknown }).entries)
        ? ((res.data as { entries: HistoryEntry[] }).entries ?? [])
        : [];
    const rows =
      entries.length === 0
        ? `<p class="app-lead">No completed cases yet. Finish a case to see it here.</p>`
        : entries
            .map((e) => {
              const date = new Date(e.playedAt).toLocaleDateString(undefined, {
                year: "numeric",
                month: "short",
                day: "numeric",
              });
              const partners =
                e.coPlayers.length > 0
                  ? escapeHtml(e.coPlayers.join(", "))
                  : "<em>Solo</em>";
              const outcome = e.solved
                ? `<span class="badge badge-solved">Solved</span>`
                : `<span class="badge badge-unsolved">Killer remains free</span>`;
              return `<article class="history-row card">
                <h3 class="history-title">${escapeHtml(e.caseTitle)}</h3>
                <p class="history-meta">${e.playerCount}-player · ${escapeHtml(date)}</p>
                <p class="history-meta">Played with: ${partners}</p>
                ${outcome}
              </article>`;
            })
            .join("");
    renderHubShell(
      "Your cases",
      "",
      `<div class="view-toolbar">${viewBackButton()}<h2 class="view-title">Game History</h2></div>
      <div class="history-list">${rows}</div>`
    );
    bindBack(() => void showSignedInHub());
  }

  async function ensureDetectivesRoster(): Promise<void> {
    if (detectivesRoster.length) return;
    const play = await api<{ detectives: PlayableDetective[] }>("/api/playables");
    if (play.ok && (play.data as { detectives?: PlayableDetective[] }).detectives?.length) {
      detectivesRoster = (play.data as { detectives: PlayableDetective[] }).detectives;
    } else {
      detectivesRoster = FALLBACK_DETECTIVES;
    }
    preloadDetectivePortraits(detectivesRoster);
  }

  function lobbyRoomHost(): HTMLElement {
    return el("lobby-room-host");
  }

  function wireLobbyLeaveButton(code: string): void {
    const leaveBtn = lobbyPanel().querySelector<HTMLElement>("[data-action='back']");
    if (!leaveBtn || leaveBtn.dataset.wired) return;
    leaveBtn.dataset.wired = "1";
    leaveBtn.onclick = () => {
      void (async () => {
        await deps.abandonCurrentLobby();
        deps.clearRejoinHint();
        setMsg("");
        await showSignedInHub();
      })();
    };
  }

  async function autoStartFromLobbyIfSessionLive(
    lobbyCode: string,
    sid: string
  ): Promise<boolean> {
    const available = await deps.checkSessionAvailable(sid);
    if (!available) {
      await deps.abandonStaleLobbySession(lobbyCode);
      return true;
    }
    deps.prefetchSession(sid);
    const reveal = await deps.fetchLobbySessionReveal(lobbyCode);
    void deps.tryStartFromLobbyAfterRoulette(sid, reveal ?? null);
    return true;
  }

  function bindLobbyInvite(code: string): void {
    const btn = lobbyPanel().querySelector<HTMLButtonElement>("[data-lobby-invite]");
    if (!btn) return;
    const activate = (): void => {
      void shareLobbyInvite(code)
        .then((result) => {
          if (result === "copied") {
            setMsg("Invite copied to clipboard.");
            window.setTimeout(() => setMsg(""), 2500);
          } else {
            setMsg("");
          }
        })
        .catch(() => setMsg("Could not copy invite — try again."));
    };
    btn.onclick = activate;
    btn.onkeydown = (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        activate();
      }
    };
  }

  function renderLobbyShell(code: string, maxPlayers: number): void {
    showLobbyPanel();
    lobbyShellCode = code;
    lobbyShellMaxPlayers = maxPlayers;
    const inviteHtml = isSoloLobby(maxPlayers) ? "" : lobbyInviteBlockHtml(code);
    lobbyPanel().innerHTML = `
      <div class="view-toolbar" data-lobby-shell-code="${escapeHtml(code)}">
        ${viewBackButton("← Leave lobby")}
        <h2 class="view-title">Lobby</h2>
      </div>
      ${inviteHtml}
      <div id="lobby-room-host"></div>
      <p id="pre-game-msg" class="pre-game-msg" role="alert"></p>`;
    if (!isSoloLobby(maxPlayers)) bindLobbyInvite(code);
    wireLobbyLeaveButton(code);
  }

  function ensureLobbyShell(code: string, maxPlayers: number): void {
    if (
      lobbyShellCode === code &&
      lobbyShellMaxPlayers === maxPlayers &&
      lobbyPanel().querySelector("[data-lobby-shell-code]")
    ) {
      return;
    }
    renderLobbyShell(code, maxPlayers);
  }

  function stashJoinDeepLinkFromUrl(): void {
    const raw = readJoinCodeFromLocation();
    if (!raw) return;
    const normalized = normalizeLobbyCode(raw);
    if (normalized) pendingJoinCode = normalized;
    clearJoinCodeFromLocation();
  }

  function lobbyApiPath(code: string, suffix: string): string {
    return `/api/lobbies/${encodeURIComponent(code)}${suffix}`;
  }

  function appendLobbyChatMessage(displayName: string, text: string): void {
    if (!lobbyChatLogEl) return;
    lobbyChatLogEl.innerHTML += `<div><b>${escapeHtml(displayName)}</b>: ${escapeHtml(text)}</div>`;
    lobbyChatLogEl.scrollTop = lobbyChatLogEl.scrollHeight;
  }

  type LobbyRoomData = {
    code: string;
    members: { userId: string; displayName: string; characterNpcId: string | null }[];
    maxPlayers: number;
    hostUserId: string;
    status?: string;
    sessionId?: string | null;
  };

  function isLobbyTypingFocused(): boolean {
    if (typeof document === "undefined") return false;
    const active = document.activeElement;
    if (!active || !(active instanceof HTMLElement)) return false;
    const id = active.id;
    return id === "lobby-display-name" || id === "lobby-chat-input";
  }

  function wireLobbyRoomInteractions(L: LobbyRoomData, code: string): void {
    const userId = deps.getUserId();
    const mine = L.members.find((m) => m.userId === userId);
    const displayNameValue = mine
      ? lobbyMemberDisplayLabel(mine, L.members)
      : me
        ? playerLabel(me)
        : "Detective";

    const displayNameInput = document.getElementById(
      "lobby-display-name"
    ) as HTMLInputElement | null;
    if (displayNameInput && !displayNameInput.dataset.wired) {
      displayNameInput.dataset.wired = "1";
      lastSavedLobbyDisplayName = displayNameValue;
      displayNameInput.addEventListener("input", () => {
        if (displayNameSaveTimer) clearTimeout(displayNameSaveTimer);
        displayNameSaveTimer = setTimeout(() => {
          const name = displayNameInput.value.trim();
          if (!name || name === lastSavedLobbyDisplayName) return;
          void api(lobbyApiPath(L.code, "/display-name"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ displayName: name }),
          })
            .then((res) => {
              if (!res.ok) {
                setMsg(
                  deps.formatApiError(res.data, "Could not save display name — try again.")
                );
                return;
              }
              lastSavedLobbyDisplayName = name;
              setMsg("");
            })
            .catch(() => {
              setMsg("Could not save display name — check your connection.");
            });
        }, 300);
      });
    }

    const pickerHost = document.getElementById("detective-picker-host");
    if (pickerHost) {
      bindDetectivePicker(pickerHost, (npcId) => {
        if (characterSaveTimer) clearTimeout(characterSaveTimer);
        characterSaveTimer = setTimeout(() => {
          characterSaveInFlight = true;
          void api(lobbyApiPath(L.code, "/character"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ characterNpcId: npcId }),
          })
            .then((res) => {
              if (!res.ok) {
                setMsg(deps.formatApiError(res.data, "Could not save detective — try again."));
                return;
              }
              setMsg("");
              if (res.data && typeof res.data === "object") {
                patchLobbyStartState(res.data as LobbyRoomData);
              }
            })
            .catch(() => {
              setMsg("Could not save detective — check your connection.");
            })
            .finally(() => {
              characterSaveInFlight = false;
            });
        }, 300);
      });
    }

    const startBtn = document.getElementById("btn-start") as HTMLButtonElement | null;
    if (startBtn && !startBtn.dataset.wired) {
      startBtn.dataset.wired = "1";
      startBtn.onclick = async () => {
        if (startBtn.disabled) return;
        startBtn.disabled = true;
        startBtn.textContent = "Starting case…";
        try {
          const ac = new AbortController();
          const timeoutId = setTimeout(() => ac.abort(), START_CASE_TIMEOUT_MS);
          let st: {
            ok: boolean;
            status: number;
            data: {
              sessionId: string;
              chosenTitle: string;
              catalog: { total: number; titles: string[] };
            };
          };
          try {
            st = await api(lobbyApiPath(L.code, "/start"), {
              method: "POST",
              signal: ac.signal,
            });
          } catch (err) {
            if (err instanceof Error && err.name === "AbortError") {
              setMsg(
                deps.formatApiError(
                  { error: "client_timeout" },
                  "Case layout is taking longer than usual — wait a moment and try starting again."
                )
              );
              return;
            }
            throw err;
          } finally {
            clearTimeout(timeoutId);
          }
          if (!st.ok) {
            const errBody = st.data as { error?: string; message?: string };
            const fallback =
              errBody?.error === "case_load_failed" &&
              typeof errBody.message === "string" &&
              errBody.message.includes("layerA_coherence")
                ? "This case file needs a fresh map layout — leave the lobby, host a new game from the hub, and try again."
                : "Could not start the case. Check that everyone picked a detective.";
            setMsg(deps.formatApiError(st.data, fallback));
            return;
          }
          const data = st.data as {
            sessionId: string;
            chosenTitle?: string;
            catalog?: { total: number; titles: string[] };
          };
          const sid = data.sessionId;
          deps.prefetchSession(sid);
          const reveal =
            data.chosenTitle && data.catalog
              ? { chosenTitle: data.chosenTitle, catalog: data.catalog }
              : await deps.fetchLobbySessionReveal(L.code);
          void deps.tryStartFromLobbyAfterRoulette(sid, reveal);
        } finally {
          if (startBtn.isConnected) {
            startBtn.disabled = false;
            startBtn.textContent = "Start case";
          }
        }
      };
    }

    wireLobbyLeaveButton(L.code);

    const chatForm = document.getElementById("lobby-chat-form") as HTMLFormElement;
    const chatInput = document.getElementById("lobby-chat-input") as HTMLInputElement;
    if (chatForm && !chatForm.dataset.wired) {
      chatForm.dataset.wired = "1";
      chatForm.addEventListener("submit", (e) => {
        e.preventDefault();
        const text = chatInput?.value.trim();
        if (!text) return;
        deps.connectLobbySocket(code);
        sendLobbyChat(code, text);
        chatInput.value = "";
      });
    }
  }

  function isDetectivePickerActive(): boolean {
    if (characterSaveInFlight) return true;
    if (typeof document === "undefined") return false;
    const active = document.activeElement;
    return active?.id === "detective-select";
  }

  function patchLobbyStartState(L: LobbyRoomData): void {
    patchLobbyRoomDom(L, { skipPicker: true });
  }

  function patchLobbyRoomDom(
    L: LobbyRoomData,
    opts: { skipPicker?: boolean } = {}
  ): void {
    const userId = deps.getUserId();
    const allPicked = L.members.every((m) => Boolean(m.characterNpcId));
    const rosterFull = L.members.length === L.maxPlayers;
    const pickedCount = L.members.filter((m) => Boolean(m.characterNpcId)).length;
    const rosterStatus = `${pickedCount}/${L.maxPlayers} detectives chosen${
      rosterFull && allPicked
        ? L.hostUserId === userId
          ? " · start when ready"
          : " · waiting for host to start"
        : ""
    }`;

    const mine = L.members.find((m) => m.userId === userId);
    const memberCards = L.members
      .map((m) =>
        lobbyMemberCardHtml(
          detectivesRoster,
          lobbyMemberDisplayLabel(m, L.members),
          m.characterNpcId
        )
      )
      .join("");

    const takenDetectives = new Set(
      L.members
        .map((m) => m.characterNpcId)
        .filter((id): id is string => Boolean(id) && !isRandomCharacterId(id))
    );
    const pickerHtml = renderDetectivePickerHtml(
      detectivesRoster,
      mine?.characterNpcId ?? null,
      takenDetectives
    );

    const statusEl = lobbyRoomHost().querySelector(".lobby-roster-status");
    if (statusEl) statusEl.textContent = rosterStatus;

    const membersEl = lobbyRoomHost().querySelector(".lobby-members");
    if (membersEl) membersEl.innerHTML = memberCards;

    const skipPicker = opts.skipPicker || isDetectivePickerActive();
    const pickerHost = document.getElementById("detective-picker-host");
    if (pickerHost && !skipPicker) pickerHost.innerHTML = pickerHtml;

    const actionsEl = lobbyRoomHost().querySelector(".lobby-actions");
    if (actionsEl) {
      const startBtn = actionsEl.querySelector<HTMLButtonElement>("#btn-start");
      if (L.hostUserId === userId) {
        if (!startBtn) {
          actionsEl.innerHTML = `<button type="button" id="btn-start" class="primary" ${!allPicked || !rosterFull ? "disabled" : ""}>Start case</button>`;
        } else {
          startBtn.disabled = !allPicked || !rosterFull;
        }
      } else if (!actionsEl.querySelector(".lobby-waiting")) {
        actionsEl.innerHTML = `<span class="lobby-waiting">Waiting for host…</span>`;
      }
    }

    wireLobbyRoomInteractions(L, L.code);
  }

  async function refreshLobbyRoom(code: string): Promise<void> {
    if (isLobbyTypingFocused() && lobbyRoomHost().querySelector(".lobby-layout")) {
      const canonical = normalizeLobbyCode(code);
      if (!canonical) return;
      const r = await api<LobbyRoomData>(lobbyApiPath(canonical, ""));
      if (!r.ok) return;
      patchLobbyRoomDom(r.data as LobbyRoomData);
      return;
    }
    await renderLobbyRoom(code);
  }

  async function renderLobbyRoom(code: string): Promise<void> {
    const canonical = normalizeLobbyCode(code);
    if (!canonical) {
      setMsg("Unknown room code.");
      return;
    }
    const userId = deps.getUserId();
    const r = await api<{
      code: string;
      members: { userId: string; displayName: string; characterNpcId: string | null }[];
      maxPlayers: number;
      hostUserId: string;
      status?: string;
      sessionId?: string | null;
    }>(lobbyApiPath(canonical, ""));
    if (!r.ok) {
      setMsg(deps.formatApiError(r.data, "Could not load this room."));
      return;
    }
    const L = r.data as (typeof r)["data"];
    ensureLobbyShell(canonical, L.maxPlayers);
    if (
      lastLobbyHostUserId &&
      L.hostUserId !== lastLobbyHostUserId &&
      L.hostUserId === userId &&
      L.status === "open"
    ) {
      setMsg("You are now the host.");
    }
    lastLobbyHostUserId = L.hostUserId;

    if (L.status === "starting" && L.members.some((m) => m.userId === userId)) {
      const sr = await api<{ sessionId: string }>(lobbyApiPath(canonical, "/session"));
      if (sr.ok) {
        startingPollCounts.delete(canonical);
        const sid = (sr.data as { sessionId: string }).sessionId;
        if (await autoStartFromLobbyIfSessionLive(canonical, sid)) return;
      }
      const polls = (startingPollCounts.get(canonical) ?? 0) + 1;
      startingPollCounts.set(canonical, polls);
      if (polls >= MAX_STARTING_POLLS) {
        lobbyRoomHost().innerHTML =
          `<p class="pre-game-msg">Start is taking too long — ask the host to try again.</p>`;
        startingPollCounts.delete(canonical);
        return;
      }
      lobbyRoomHost().innerHTML = `<p class="app-lead">Loading case data…</p>`;
      window.setTimeout(() => void renderLobbyRoom(canonical), 2000);
      return;
    }
    startingPollCounts.delete(code);

    if (L.status === "closed" && L.members.some((m) => m.userId === userId)) {
      let sid = L.sessionId ?? null;
      if (!sid) {
        const sr = await api<{ sessionId: string }>(lobbyApiPath(canonical, "/session"));
        if (sr.ok) sid = (sr.data as { sessionId: string }).sessionId;
      }
      if (sid && (await autoStartFromLobbyIfSessionLive(canonical, sid))) return;
    }

    const mine = L.members.find((m) => m.userId === userId);
    if (!mine && L.status === "open") {
      setMsg("This lobby is no longer available.");
      await deps.abandonCurrentLobby();
      await showSignedInHub();
      return;
    }

    const allPicked = L.members.every((m) => Boolean(m.characterNpcId));
    const rosterFull = L.members.length === L.maxPlayers;
    const pickedCount = L.members.filter((m) => Boolean(m.characterNpcId)).length;
    const rosterStatus = `${pickedCount}/${L.maxPlayers} detectives chosen${
      rosterFull && allPicked
        ? L.hostUserId === userId
          ? " · start when ready"
          : " · waiting for host to start"
        : ""
    }`;

    const displayNameValue = mine
      ? lobbyMemberDisplayLabel(mine, L.members)
      : me
        ? playerLabel(me)
        : "Detective";

    const memberCards = L.members
      .map((m) =>
        lobbyMemberCardHtml(
          detectivesRoster,
          lobbyMemberDisplayLabel(m, L.members),
          m.characterNpcId
        )
      )
      .join("");

    const takenDetectives = new Set(
      L.members
        .map((m) => m.characterNpcId)
        .filter((id): id is string => Boolean(id) && !isRandomCharacterId(id))
    );
    const pickerHtml = renderDetectivePickerHtml(
      detectivesRoster,
      mine?.characterNpcId ?? null,
      takenDetectives
    );

    currentView = "lobby-room";
    const host = lobbyRoomHost();
    const nameSnapshot =
      typeof document !== "undefined" &&
      document.activeElement?.id === "lobby-display-name"
        ? {
            value: (document.getElementById("lobby-display-name") as HTMLInputElement).value,
            selectionStart: (document.getElementById("lobby-display-name") as HTMLInputElement)
              .selectionStart,
            selectionEnd: (document.getElementById("lobby-display-name") as HTMLInputElement)
              .selectionEnd,
          }
        : null;
    host.innerHTML = `
      <div class="lobby-layout${isSoloLobby(L.maxPlayers) ? " lobby-layout-solo" : ""}">
        <div class="lobby-layout-main">
          <p class="lobby-roster-status">${escapeHtml(rosterStatus)}</p>
          <label class="lobby-display-name">
            <span class="lobby-display-name-label">Display name</span>
            <input
              id="lobby-display-name"
              type="text"
              maxlength="24"
              value="${escapeHtml(displayNameValue)}"
              autocomplete="nickname"
            />
          </label>
          <div class="lobby-members lobby-member-cards">${memberCards}</div>
          <p class="detective-picker-label">Choose your detective</p>
          <div id="detective-picker-host">${pickerHtml}</div>
          <div class="lobby-actions">
            ${
              L.hostUserId === userId
                ? `<button type="button" id="btn-start" class="primary" ${!allPicked || !rosterFull ? "disabled" : ""}>Start case</button>`
                : `<span class="lobby-waiting">Waiting for host…</span>`
            }
          </div>
        </div>
        ${isSoloLobby(L.maxPlayers) ? "" : lobbyChatBlockHtml()}
      </div>`;

    lobbyChatLogEl = isSoloLobby(L.maxPlayers)
      ? null
      : (document.getElementById("lobby-chat-log") as HTMLDivElement);

    wireLobbyRoomInteractions(L, code);

    if (nameSnapshot) {
      const input = document.getElementById("lobby-display-name") as HTMLInputElement | null;
      if (input) {
        input.value = nameSnapshot.value;
        input.focus();
        if (nameSnapshot.selectionStart != null && nameSnapshot.selectionEnd != null) {
          input.setSelectionRange(nameSnapshot.selectionStart, nameSnapshot.selectionEnd);
        }
      }
    }
  }

  let sendLobbyChatFn: ((code: string, text: string) => void) | null = null;

  function sendLobbyChat(code: string, text: string): void {
    sendLobbyChatFn?.(code, text);
  }

  function setSendLobbyChat(fn: (code: string, text: string) => void): void {
    sendLobbyChatFn = fn;
  }

  async function createLobby(maxPlayers: 1 | 2): Promise<void> {
    deps.clearPostCaseBanner();
    deps.clearLobbySessionStartBlocks();
    await deps.abandonCurrentLobby();
    const r = await api<{ code: string }>("/api/lobbies", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ maxPlayers }),
    });
    if (!r.ok) {
      setMsg("Could not create room");
      return;
    }
    const code = (r.data as { code: string }).code;
    await ensureDetectivesRoster();
    await renderLobbyRoom(code);
    deps.connectLobbySocket(code);
  }

  async function joinLobbyByCode(raw: string): Promise<void> {
    const code = normalizeLobbyCode(raw.trim());
    if (!raw.trim()) {
      setMsg("Enter a city code.");
      return;
    }
    if (!code) {
      setMsg("Unknown city code — check with your host.");
      return;
    }
    const r = await api<{ code: string }>(lobbyApiPath(code, "/join"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    });
    if (!r.ok) {
      setMsg(deps.formatApiError(r.data, "Could not join this room."));
      return;
    }
    clearJoinCodeFromLocation();
    const resolved = (r.data as { code?: string })?.code ?? code;
    await ensureDetectivesRoster();
    await renderLobbyRoom(resolved);
    deps.connectLobbySocket(resolved);
  }

  async function joinLobby(): Promise<void> {
    const raw = (document.getElementById("join-code") as HTMLInputElement).value;
    await joinLobbyByCode(raw);
  }

  async function consumeJoinDeepLink(): Promise<void> {
    if (!pendingJoinCode || !me) return;
    const code = pendingJoinCode;
    pendingJoinCode = null;
    await joinLobbyByCode(code);
  }

  refreshLobbyView = refreshLobbyRoom;

  function setMessage(text: string): void {
    setMsg(text);
  }

  stashJoinDeepLinkFromUrl();

  return {
    renderLanding,
    renderSignIn,
    showSignedInHub,
    signOut,
    setMessage,
    getMe: () => me,
    getRefreshLobbyView: () => refreshLobbyView,
    appendLobbyChatMessage,
    setSendLobbyChat,
    fetchMe,
    consumeJoinDeepLink,
    stashJoinDeepLinkFromUrl,
    resetLobbyState,
  };
}
